// Pre-add allergy cross-reactivity takeover modal.
//
// Renders when the SPA's pre-add precheck (POST /api/clinical/allergy-precheck)
// returns one or more matches. The user must explicitly confirm
// "I have spoken to my pharmacist" before the medication is saved —
// this is the deliberate gate that closes the round-4 #1 safety gap.
//
// Senior-tuned: full-screen red banner, large copy, two-step confirm,
// keyboard-accessible (Esc dismisses = cancel the add).

import { useEffect, useState } from 'react';
import type { AllergyPrecheckMatch } from '../api/allergies';

interface Props {
  /** The match(es) returned by /api/clinical/allergy-precheck. We render
   *  the highest-severity match prominently; the rest are listed below. */
  matches: AllergyPrecheckMatch[];
  /** The medication name the user was about to add (for the prompt). */
  medicationName: string;
  /** Cancel: don't add the medication. */
  onCancel: () => void;
  /** Proceed: the user has explicitly confirmed they've talked to their
   *  pharmacist. Save the medication anyway. */
  onConfirmAddAnyway: () => void;
}

const SEVERITY_RANK: Record<AllergyPrecheckMatch['severity'], number> = {
  CRITICAL: 0,
  MAJOR: 1,
  MODERATE: 2,
  MINOR: 3,
};

const SEVERITY_BG: Record<AllergyPrecheckMatch['severity'], string> = {
  CRITICAL: '#7f1d1d',
  MAJOR: '#9a3412',
  MODERATE: '#92400e',
  MINOR: '#1f2937',
};

const SEVERITY_LABEL: Record<AllergyPrecheckMatch['severity'], string> = {
  CRITICAL: 'CRITICAL ALLERGY ALERT',
  MAJOR: 'IMPORTANT ALLERGY ALERT',
  MODERATE: 'Allergy alert',
  MINOR: 'Allergy note',
};

export function AllergyAlertPanel({
  matches,
  medicationName,
  onCancel,
  onConfirmAddAnyway,
}: Props): JSX.Element {
  const [confirmTyped, setConfirmTyped] = useState('');
  const sorted = [...matches].sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]);
  const top = sorted[0];
  const others = sorted.slice(1);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  if (!top) {
    // Defensive: shouldn't render with empty matches
    return <></>;
  }

  const requiredPhrase = 'I understand';
  const canConfirm = confirmTyped.trim().toLowerCase() === requiredPhrase.toLowerCase();

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="allergy-alert-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1100,
        background: 'rgba(15, 23, 42, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        style={{
          background: 'var(--color-surface)',
          borderRadius: 20,
          maxWidth: 560,
          width: '100%',
          maxHeight: '95vh',
          overflowY: 'auto',
          boxShadow: '0 24px 56px rgba(0, 0, 0, 0.35)',
        }}
      >
        {/* Red banner */}
        <div
          style={{
            background: SEVERITY_BG[top.severity],
            color: '#fff',
            padding: '20px 24px',
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: '0.08em',
              opacity: 0.95,
            }}
          >
            {SEVERITY_LABEL[top.severity]}
          </p>
          <h2
            id="allergy-alert-title"
            style={{ margin: '6px 0 0', fontSize: 26, lineHeight: 1.2, fontWeight: 800 }}
          >
            {medicationName} may cross-react with your {top.allergen} allergy.
          </h2>
        </div>

        <div style={{ padding: 24, display: 'grid', gap: 16 }}>
          <p style={{ margin: 0, fontSize: 17, lineHeight: 1.5 }}>{top.explanation}</p>

          {others.length > 0 ? (
            <div
              style={{
                padding: 12,
                background: 'var(--color-surface-secondary)',
                borderRadius: 12,
                fontSize: 15,
              }}
            >
              <p style={{ margin: '0 0 6px', fontWeight: 700 }}>Other allergies on file:</p>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {others.map((m) => (
                  <li key={`${m.groupId}-${m.allergen}`}>
                    {m.allergen} ({m.groupLabel})
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <p
            style={{
              margin: 0,
              fontSize: 14,
              color: 'var(--color-text-secondary)',
              fontStyle: 'italic',
            }}
          >
            Source: {top.citation}
          </p>

          <p
            style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--color-text-primary)' }}
          >
            Talk to your pharmacist or doctor before taking the next dose.
          </p>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={onCancel}
              style={{
                minHeight: 56,
                padding: '0 22px',
                background: 'var(--color-primary)',
                color: '#fff',
                border: 0,
                borderRadius: 12,
                fontSize: 17,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Don't add it
            </button>
          </div>

          <details style={{ marginTop: 8 }}>
            <summary
              style={{
                cursor: 'pointer',
                fontSize: 14,
                color: 'var(--color-text-secondary)',
                listStyle: 'none',
              }}
            >
              I have already spoken to my pharmacist about this. Let me add it anyway.
            </summary>
            <div
              style={{
                marginTop: 12,
                padding: 14,
                background: 'var(--color-surface-secondary)',
                borderRadius: 12,
              }}
            >
              <p style={{ margin: '0 0 10px', fontSize: 15 }}>
                To confirm, type the words <strong>{requiredPhrase}</strong> below.
              </p>
              <input
                type="text"
                value={confirmTyped}
                onChange={(e) => setConfirmTyped(e.target.value)}
                aria-label={`Type "${requiredPhrase}" to confirm`}
                placeholder={requiredPhrase}
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  fontSize: 17,
                  border: '1.5px solid var(--color-border)',
                  borderRadius: 8,
                  marginBottom: 12,
                  boxSizing: 'border-box',
                }}
              />
              <button
                type="button"
                disabled={!canConfirm}
                onClick={onConfirmAddAnyway}
                style={{
                  minHeight: 48,
                  padding: '0 18px',
                  background: canConfirm ? '#991b1b' : 'var(--color-border)',
                  color: canConfirm ? '#fff' : 'var(--color-text-secondary)',
                  border: 0,
                  borderRadius: 10,
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: canConfirm ? 'pointer' : 'not-allowed',
                }}
              >
                Add medication anyway
              </button>
            </div>
          </details>
        </div>
      </div>
    </div>
  );
}
